import { Box, LinearProgress } from "@mui/material";
import { green } from "@mui/material/colors";
import isArray from "lodash/isArray";
import { useMemo } from "react";

import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

const FILL_COLOR = green[800];

const UnderwritingRequirementsCompletedProgressBar = ({
  loan,
  showOnlyProgressBar = false,
  showStatusCounts = false,
}) => {
  const stats = useMemo(() => {
    const underwritingRequirements = isArray(loan?.underwritingRequirements)
      ? loan.underwritingRequirements
      : [];

    const total = underwritingRequirements.length;

    if (total === 0) {
      return {
        total: 0,
        accepted: 0,
        pendingReview: 0,
        receivedPendingReview: 0,
        incomplete: 0,
        rejected: 0,
        other: 0,
        progress: 0,
      };
    }

    let accepted = 0;
    let pendingReview = 0;
    let receivedPendingReview = 0;
    let incomplete = 0;
    let rejected = 0;
    let other = 0;

    underwritingRequirements.forEach((req) => {
      // Requirement completion is based on req.status
      const status = req?.status;
      const docStipStatus = req?.docStipStatus;

      if (status === "Accepted") {
        accepted += 1;
        return;
      }

      // Optional buckets for visibility (based on either status or docStipStatus)
      if (docStipStatus === "Received - Pending Review") {
        receivedPendingReview += 1;
      } else if (
        status === "Pending Review" ||
        docStipStatus === "Pending Review"
      ) {
        pendingReview += 1;
      } else if (status === "Incomplete" || docStipStatus === "Incomplete") {
        incomplete += 1;
      } else if (status === "Rejected" || docStipStatus === "Rejected") {
        rejected += 1;
      } else {
        other += 1;
      }
    });

    return {
      total,
      accepted,
      pendingReview,
      receivedPendingReview,
      incomplete,
      rejected,
      other,
      progress: total > 0 ? (accepted / total) * 100 : 0,
    };
  }, [loan]);

  if (showOnlyProgressBar) {
    return (
      <LinearProgress
        variant="determinate"
        value={stats.progress}
        sx={{
          flexGrow: 1,
          height: 8,
          borderRadius: 4,
          backgroundColor: "#e2e8f0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: FILL_COLOR,
          },
        }}
      />
    );
  }

  return (
    <Flx col g={1} sx={{ flexGrow: 1 }}>
      <Flx g={2} ac sx={{ flexGrow: 1 }}>
        <LinearProgress
          variant="determinate"
          value={stats.progress}
          sx={{
            flexGrow: 1,
            height: 8,
            borderRadius: 4,
            width: "100%",
            backgroundColor: "#e2e8f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: FILL_COLOR,
            },
          }}
        />
        <Flx ac gap={0.6} sx={{ flexShrink: 0 }}>
          <Txt secondary>
            {stats.accepted} of {stats.total}
          </Txt>
          <Txt secondary>Complete</Txt>
        </Flx>
      </Flx>

      {showStatusCounts && stats.total > 0 && (
        <Flx g={2} wrap sx={{ fontSize: "0.875rem" }}>
          {stats.accepted > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: FILL_COLOR, fontWeight: 600 }}>
                {stats.accepted}
              </Box>{" "}
              Accepted
            </Txt>
          )}

          {stats.receivedPendingReview > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                {stats.receivedPendingReview}
              </Box>{" "}
              Received - Pending Review
            </Txt>
          )}

          {stats.pendingReview > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                {stats.pendingReview}
              </Box>{" "}
              Pending Review
            </Txt>
          )}

          {stats.incomplete > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#f97316", fontWeight: 600 }}>
                {stats.incomplete}
              </Box>{" "}
              Incomplete
            </Txt>
          )}

          {stats.rejected > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#ef4444", fontWeight: 600 }}>
                {stats.rejected}
              </Box>{" "}
              Rejected
            </Txt>
          )}

          {stats.other > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#64748b", fontWeight: 600 }}>
                {stats.other}
              </Box>{" "}
              Other
            </Txt>
          )}
        </Flx>
      )}
    </Flx>
  );
};

export default UnderwritingRequirementsCompletedProgressBar;
